import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function GuidePage() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || (user.permissions && user.permissions.length > 0));
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const load = () => api.get("/guide").then(({ data }) => { setContent(data.content || ""); setUpdatedAt(data.updated_at); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/admin/guide", { content: draft });
      setContent(data.content); setEditing(false); toast.success("Guide updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-ys-sky text-white flex items-center justify-center"><BookOpen className="w-6 h-6" /></div>
            <div>
              <h1 className="font-display text-2xl font-extrabold">User Guide &amp; Policies</h1>
              <p className="text-slate-500 text-sm">How Yard-skip works, plus our guidelines and policies.</p>
            </div>
          </div>
          {isAdmin && !editing && (
            <Button onClick={() => { setDraft(content); setEditing(true); }} data-testid="guide-edit-btn" className="rounded-full gap-1 bg-ys-sky hover:bg-sky-700"><Pencil className="w-4 h-4" />Edit</Button>
          )}
        </div>

        {editing ? (
          <div className="mt-6" data-testid="guide-editor">
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={22} className="rounded-2xl text-sm" data-testid="guide-textarea" />
            <div className="flex gap-2 mt-3">
              <Button onClick={save} data-testid="guide-save-btn" className="rounded-full gap-1 bg-ys-sky hover:bg-sky-700"><Save className="w-4 h-4" />Save</Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full gap-1"><X className="w-4 h-4" />Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 whitespace-pre-wrap leading-relaxed text-slate-700" data-testid="guide-content">{content || "No guide yet."}</div>
        )}
        {updatedAt && !editing && <p className="text-xs text-slate-400 mt-3">Last updated {new Date(updatedAt).toLocaleDateString()}</p>}
      </div>
    </div>
  );
}
